import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import useSWR from 'swr';
import qs from 'qs';
import {
  locationT,
  Marketplace,
  OutstandingInvoices,
} from '@ordermentum/ui-types';
import { fetcher } from '@ordermentum/ui-shared/swr/fetchers';
import { NewPaymentMethod } from '@ordermentum/ui-shared/organisms/PaymentMethods';
import Button from '@ordermentum/ui-shared/components/Button/Button';
import Spinner from '@ordermentum/ui-shared/foundations/Spinner';
import StandaloneFooter from '@ordermentum/ui-shared/components/StandaloneFooter';
import { Invoice, ToastReduxAction } from '@ordermentum/ui-definitions';
import { PaymentMethodType } from '@ordermentum/ui-types/src/purchaser';
import { useFlags } from '@ordermentum/ui-shared/hooks/useFlags';
import {
  addNewDirect,
  DirectDebitParams,
} from '@ordermentum/ui-shared/redux/payment_methods';
import * as LocalStorageUtility from '@ordermentum/ui-shared/utilities/localStorage';
import { Icon } from '@ordermentum/ui-shared/foundations';
import { handleExportPdf } from '../../redux/actions';
import history from '../../lib/history';
import { SUPPLIER_ASYNC_PAYMENTS } from '../Home';
import { showToast } from '../../redux/actions/toast';
import { usePciRedirect } from './usePciRedirect';

type Props = {
  location: locationT;
  params: any;
  paymentType: any[];
  handleExport: (paymentRequestId?: string, token?: string) => void;
  addDirectPayment: (
    directDetails: DirectDebitParams,
    retailerId: string
  ) => Promise<void>;
  showToastAction: (toast: ToastReduxAction) => Promise<void>;
};

export const PaymentRequest = ({
  params,
  location,
  paymentType,
  showToastAction,
  handleExport,
  addDirectPayment,
}: Props) => {
  const { retailerId } = params;
  const { paymentRequestId, invoiceId, userId, token, supplierId } =
    location.query;
  // defaultPaymentType is used to prevent redirect loops when coming back from the card page
  const { defaultPaymentType } = location.query;

  const flags = useFlags([SUPPLIER_ASYNC_PAYMENTS], {
    supplierId,
    source: 'web',
  });

  const { data: outstandingInvoices } = useSWR<OutstandingInvoices>(
    `/v1/payment-requests/${paymentRequestId}/outstanding-invoices?${qs.stringify(
      { token }
    )}`,
    fetcher()
  );

  let apiUrl = `/v1/retailers/${retailerId}`;
  if (paymentRequestId && token) {
    const qst = qs.stringify({
      retailerId,
      token,
    });
    apiUrl = `/v1/payment-requests/${paymentRequestId}?${qst}`;
  }

  const { data, error, isLoading } = useSWR<{
    id: string;
    purchaser: Marketplace;
    invoice?: Invoice;
    enableSecurePaymentRequest?: boolean;
  }>(apiUrl, fetcher());

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const invoices = outstandingInvoices?.data?.invoices ?? [];
  const totalOutstandingInvoices = invoices.length;
  const source =
    totalOutstandingInvoices > 0 ? 'outstanding-invoices' : 'payment-request';

  const onSuccess = (
    paymentId: string,
    userData?: {
      signatory: string;
      email: string;
      phone: string;
    }
  ) => {
    const redirectUrl =
      totalOutstandingInvoices > 0
        ? `/payments/${retailerId}/outstanding-invoices`
        : `/payments/${retailerId}/auth`;

    if (userData && !userId) {
      LocalStorageUtility.setItem(
        `dd-$${paymentRequestId}`,
        JSON.stringify(userData)
      );
    }
    history.push({
      pathname: redirectUrl,
      query: {
        paymentRequestId,
        invoiceId,
        userId,
        token,
      },
    });
  };

  const paymentMethodTypes = useMemo(
    () => data?.purchaser?.paymentMethodTypes?.value ?? [],
    [data?.purchaser?.paymentMethodTypes?.value]
  );
  const canSwitch =
    paymentMethodTypes.includes('card') &&
    paymentMethodTypes.includes('direct');

  const [accountType, setAccountType] = useState<PaymentMethodType>('card');
  const isCard = accountType === 'card';

  const isInvoice = Boolean(data?.invoice) && Boolean(invoiceId);
  const purchaser = data?.purchaser;

  useEffect(() => {
    // If defaultPaymentType is provided via query param (e.g., from switching back from card page),
    // use that instead of inferring from available payment methods
    if (defaultPaymentType === 'direct' || defaultPaymentType === 'card') {
      setAccountType(defaultPaymentType);
      return;
    }

    const inferredAccountType =
      paymentMethodTypes.length > 0 && !paymentMethodTypes.includes('card')
        ? 'direct'
        : 'card';
    setAccountType(inferredAccountType);
  }, [paymentMethodTypes, defaultPaymentType]);

  const enableSecurePaymentRequest = data?.enableSecurePaymentRequest;

  const {
    shouldRedirectToSecureCardPage,
    isPciSecureCardFormEnabled,
    triggerRedirect,
  } = usePciRedirect({
    retailerId,
    supplierId,
    paymentRequestId,
    invoiceId,
    userId,
    token,
    isCard,
    isInvoice: Boolean(isInvoice),
    isLoading,
    hasError: Boolean(error),
    hasData: Boolean(data),
    purchaserId: purchaser?.id,
    canSwitch,
    totalOutstandingInvoices,
    defaultPaymentType,
    // Use enableSecurePaymentRequest from payment request response to avoid extra flag API call
    enableSecurePaymentRequest,
  });

  // Letting the component handle the feedback to the user
  // while the hook handles the redirect in the background
  if (shouldRedirectToSecureCardPage) {
    return (
      <div>
        <Spinner size="M" />
      </div>
    );
  }

  if (isLoading || !flags)
    return (
      <div>
        <Spinner size="M" />
      </div>
    );

  if (error || !data)
    return (
      <div className="flex h-screen flex-col items-center justify-center font-body text-body font-bold">
        <div>Failed to load required data</div>
        <div>
          <a href="/" className="text-primary-brand hover:underline">
            Go home
          </a>
        </div>
      </div>
    );

  // Re-extract with narrowed types after we know data exists
  const { purchaser: purchaserData, invoice: invoiceData } = data;
  const { supplier: supplierData, retailer: retailerData } = purchaserData;

  const handleSwitchPaymentType = () => {
    if (isCard) {
      setAccountType('direct');
      return;
    }

    if (isPciSecureCardFormEnabled) {
      triggerRedirect();
      return;
    }

    setAccountType('card');
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-regular">
      <div className="flex justify-center px-s pb-xl pt-16">
        <div className="flex w-full max-w-screen-sm flex-col self-stretch">
          {Boolean(supplierData) && (
            <div className="flex flex-col items-center gap-xxs">
              <img
                className="size-14 max-h-24 rounded border border-solid border-background-subtle"
                src={supplierData.logo?.mini}
                alt={`${supplierData.tradingName ?? '--'} logo`}
              />
              <h2 className="text-center font-heading text-h2">
                {isInvoice
                  ? `Invoice payment to ${supplierData.tradingName ?? '--'}`
                  : `${supplierData.tradingName ?? '--'} would like you to pay through Ordermentum`}
              </h2>
            </div>
          )}
          {isInvoice ? (
            <div className="flex flex-col items-center gap-s pt-s">
              <p className="font-body text-body text-subtle">
                Invoice #{invoiceData?.number ?? '--'}
              </p>
              <h3 className="text-3xl font-bold">
                ${invoiceData?.display?.totalDue ?? '--'}
              </h3>
              <Button
                icon={<Icon name="mDownload" inheritFill />}
                variant="tertiary"
                type="button"
                onClick={() => {
                  handleExport(paymentRequestId, token);
                }}
              >
                Download invoice
              </Button>
            </div>
          ) : (
            <span className="pt-s font-body text-body">
              Simply enter your {isCard ? 'card' : 'direct debit'} details.
              Future orders for{' '}
              <strong>{retailerData.tradingName ?? '--'}</strong> will use this
              payment method by default.
            </span>
          )}
          {canSwitch && !isInvoice && (
            <div>
              <Button
                variant="text-link"
                type="reset"
                onClick={handleSwitchPaymentType}
              >
                {isCard
                  ? 'Switch to a direct debit account'
                  : 'Switch to a credit card'}
              </Button>
            </div>
          )}

          <NewPaymentMethod
            showToast={showToastAction}
            params={params}
            type={accountType}
            location={location}
            history={history}
            purchaserId={purchaserData.id}
            retailer={retailerData}
            onSuccess={onSuccess}
            paymentType={paymentType}
            addDirectPayment={addDirectPayment}
            source={source}
            showAuthority
          />
        </div>
      </div>
      <StandaloneFooter />
    </div>
  );
};

const mapDispatchToProps = {
  handleExport: handleExportPdf,
  addDirectPayment: addNewDirect,
  showToastAction: showToast,
};

const PaymentRequestConnected = connect(
  null,
  mapDispatchToProps
)(PaymentRequest);

export { PaymentRequestConnected };
